/*
 * GameInput.tsx
 * Game input controls component for guess submission.
 * Includes text input field, submit button, autocomplete, and toast feedback.
 */
'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import Fuse from 'fuse.js';

// Components
import WarbleInput, { WarbleInputHandle } from '../effects/WarbleInput';

// Config
import { ANIMATION, DEBUG_LOGS, WARBLE } from '../config/constants';

// Utils
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { useCoverStory } from '@/providers/CoverStoryContext';

// Styles
import styles from './GameInput.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// Assets
import IconClear from '@/assets/svg/Game/icon-game-x_01.svg';
import IconArrowRight from "@/assets/svg/arrow-tail-right-no-padding.svg";

// Debug
import { useDebug } from "@/utils/useDebugLog";

// --------------------

interface GameInputProps {
  guess: string;
  onGuessChange: (value: string) => void;
  onSubmit: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  gameOver: boolean;
  correctAnswer?: string;
  disabled?: boolean;
  showIncorrectFeedback?: boolean;
  copy?: {
    placeholderGuessAlbum: string;
    clearButtonAriaLabel: string;
    submitButtonAriaLabel: string;
    incorrectGuess: string;
  };
}

// Memoized autocomplete item to prevent re-renders on hover
const AutocompleteItem = React.memo<{
  item: { album: string; artist: string; display: string };
  isHighlighted: boolean;
  guess: string;
  onSelect: (item: { album: string; artist: string; display: string }) => void;
  index: number;
}>(({ item, isHighlighted, guess, onSelect, index }) => {
  const highlightMatches = useMemo(() => {
    const query = guess;
    const text = item.display;
    if (!query.trim()) return [text];

    // Split query into individual words and filter out empty strings
    const queryWords = query.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    if (queryWords.length === 0) return [text];

    const textLower = text.toLowerCase();
    const matches: Array<{ start: number; end: number }> = [];
    
    queryWords.forEach(word => {
      // For single-character words, only match if surrounded by word boundaries
      if (word.length === 1) {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        let match;
        while ((match = regex.exec(textLower)) !== null) {
          matches.push({ start: match.index, end: match.index + word.length });
        }
      } else {
        // For multi-character words, do substring matching
        let index = textLower.indexOf(word);
        while (index !== -1) {
          matches.push({ start: index, end: index + word.length });
          index = textLower.indexOf(word, index + 1);
        }
      }
    });

    matches.sort((a, b) => a.start - b.start);
    const mergedMatches: Array<{ start: number; end: number }> = [];
    
    matches.forEach(match => {
      if (mergedMatches.length === 0) {
        mergedMatches.push(match);
      } else {
        const last = mergedMatches[mergedMatches.length - 1];
        if (match.start <= last.end) {
          // Overlapping or adjacent - merge
          last.end = Math.max(last.end, match.end);
        } else {
          mergedMatches.push(match);
        }
      }
    });

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    mergedMatches.forEach((match, idx) => {
      if (match.start > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex, match.start)}</span>);
      }
      parts.push(<strong key={`bold-${match.start}-${idx}`}>{text.substring(match.start, match.end)}</strong>);
      lastIndex = match.end;
    });

    if (lastIndex < text.length) {
      parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
    }

    return parts;
  }, [item.display, guess]);

  return (
    <button
      className={classNames.bind(styles)('autocomplete-item', { 'highlighted': isHighlighted })}
      onClick={() => onSelect(item)}
      onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
      type="button"
      role="option"
      aria-selected={isHighlighted}
      tabIndex={0}
      id={`album-option-${index}`}
    >
      {highlightMatches.map(part => part)}
    </button>
  );
});

AutocompleteItem.displayName = 'AutocompleteItem';

const GameInput: React.FC<GameInputProps> = ({
  guess,
  onGuessChange,
  onSubmit,
  onKeyDown,
  gameOver,
  correctAnswer,
  disabled = false,
  showIncorrectFeedback = false,
  copy,
}) => {
  const { log } = useDebug({ name: 'GameInput', bgColor: 'hotpink', enabled: DEBUG_LOGS });

  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  // SSR guards
  const { isReady: isViewSizeReady } = useViewSize();
  const { mounted } = useClientMount();

  // Get all albums from context
  const { allAlbums } = useCoverStory();

  const [isFocused, setIsFocused] = useState(false);
  const [selectedFromAutocomplete, setSelectedFromAutocomplete] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const warbleRef = useRef<WarbleInputHandle>(null);
  const inputControlsRef = useRef<HTMLDivElement>(null);
  const toastIncorrectRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fuse: Initialize for fuzzy search on allAlbums
  const fuse = useMemo(() => {
    return new Fuse(allAlbums, {
      keys: ['search_name'],
      includeScore: true,
      useExtendedSearch: true,
      ignoreLocation: true,
      findAllMatches: true,
      threshold: 0.3,
      minMatchCharLength: 1,
    });
  }, [allAlbums]);

  // Fuse: Get autocomplete suggestions (returns objects with album, artist, and display text)
  const autocompleteItems = useMemo(() => {
    if (!guess.trim() || selectedFromAutocomplete) {
      return [];
    }

    const results = fuse
      .search(guess)
      .slice(0, 6)
      .map(result => ({
        album: result.item.album || '',
        artist: result.item.artist_name || '',
        display: `${result.item.album || ''}${result.item.artist_name ? ' - ' + result.item.artist_name : ''}`
      }))
      .filter(item => item.album);

    return results;
  }, [guess, fuse, selectedFromAutocomplete]);

  // Reset highlighted index when autocomplete items change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [autocompleteItems]);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Check if the current guess is a valid album name (case-insensitive exact match)
  const isValidGuess = useMemo(() => {
    if (!guess.trim()) return false;
    const normalizedGuess = guess.trim().toLowerCase();
    return allAlbums.some(album => album.album?.toLowerCase() === normalizedGuess);
  }, [guess, allAlbums]);

  const showAutocomplete = isFocused && guess.trim().length > 0 && !selectedFromAutocomplete;
  const showClearButton = guess.trim().length > 0 && !showIncorrectFeedback;
  const placeholderText = copy?.placeholderGuessAlbum + '…'; // Note: Appending ellipsis

  // -------------------- Event Handlers

  // Clear input field
  const handleClear = () => {
    onGuessChange('');
    setSelectedFromAutocomplete(false);

    // Clear any pending blur timeout before refocusing
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    inputRef.current?.focus();
  };

  // On input focus
  const handleFocus = () => {
    // Clear any pending blur timeout to prevent race condition
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsFocused(true);

    // Force scroll on iOS to ensure keyboard pushes content up on first focus
    if (isIOS) {
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ block: 'center' });
      }, 100);
    }
  };

  // On input blur
  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      blurTimeoutRef.current = null;
    }, ANIMATION.AUTOCOMPLETE_BLUR_DELAY);
  };

  // Autocomplete item select
  const handleAutocompleteSelect = (item: { album: string; artist: string; display: string }) => {
    onGuessChange(item.album); // Only set the album name as the input value
    setSelectedFromAutocomplete(true);
    // Note: Don't set isFocused to false here - input remains focused and user may continue typing
  };

  // Input field change
  const handleInputChange = (value: string) => {
    onGuessChange(value);
    setSelectedFromAutocomplete(false);
  };

  // Autocomplete keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If autocomplete is showing, handle arrow keys, Tab, and Enter
    if (showAutocomplete && autocompleteItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < autocompleteItems.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Tab') {
        e.preventDefault();
        // Tab cycles forward through items
        setHighlightedIndex(prev =>
          prev < autocompleteItems.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault();
        handleAutocompleteSelect(autocompleteItems[highlightedIndex]);
        return;
      }
    }

    // Prevent Enter key submission if guess is not valid
    if (e.key === 'Enter' && !isValidGuess) {
      e.preventDefault();
      return;
    }

    // Pass through to parent handler
    onKeyDown(e);
  };

  // -------------------- Animations

  // Incorrect feedback: warble, shake, and toast
  useGSAP(() => {
    if (!showIncorrectFeedback) return;

    // Trigger warble effect
    warbleRef.current?.triggerWarble();

    // Shake input controls
    if (inputControlsRef.current) {
      const tl = gsap.timeline();

      // Initial left movement
      tl.to(inputControlsRef.current, {
        x: -ANIMATION.SHAKE_DISTANCE,
        duration: ANIMATION.SHAKE_DURATION / 1000,
        ease: 'power2.out'
      });

      // Shake back and forth (loop)
      for (let i = 0; i < ANIMATION.SHAKE_TIMES; i++) {
        const amplitude = ANIMATION.SHAKE_DISTANCE - (i * (ANIMATION.SHAKE_DISTANCE / ANIMATION.SHAKE_TIMES));
        tl.to(inputControlsRef.current, {
          x: amplitude,
          duration: ANIMATION.SHAKE_DURATION / 1000,
          ease: 'power2.inOut'
        }).to(inputControlsRef.current, {
          x: -amplitude,
          duration: ANIMATION.SHAKE_DURATION / 1000,
          ease: 'power2.inOut'
        });
      }

      // Return to original position
      tl.to(inputControlsRef.current, {
        x: 0,
        duration: ANIMATION.SHAKE_DURATION / 1000,
        ease: 'power2.inOut'
      }).timeScale(ANIMATION.SHAKE_SPEED);
    }

    // Animate toast
    if (toastIncorrectRef.current) {
      const holdDuration = (ANIMATION.INCORRECT_FEEDBACK_DURATION - ANIMATION.TOAST_FADE_IN - ANIMATION.TOAST_FADE_OUT) / 1000;

      const tl = gsap.timeline();
      tl.fromTo(toastIncorrectRef.current, {
        opacity: 0,
        y: 10,
        scale:
        0.8
      }, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: ANIMATION.TOAST_FADE_IN / 1000,
        ease: 'back.out(1.7)'
      }).to(toastIncorrectRef.current, {
        opacity: 0,
        duration: ANIMATION.TOAST_FADE_OUT / 1000,
        ease: 'power1.out'
      }, `+=${holdDuration}`);
    }
  }, { dependencies: [showIncorrectFeedback] });

  // -------------------- Render

  if (!mounted || !isViewSizeReady) return null;

  return (
    <div className={cx('game-input')}>
      <div ref={inputControlsRef} className={cx('input-controls')}>
        <WarbleInput
          ref={warbleRef}
          className={guess.trim().length > 0 ? 'has-value' : ''}
          config={WARBLE}
        >
          <div className={cx('input-wrapper')}>

            {/* Text input */}
            <input ref={inputRef}
              id="album-guess-input"
              className={cx('text-m', 'guess-input', { 'incorrect': showIncorrectFeedback })}
              type="text"
              placeholder={placeholderText}
              value={guess}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={gameOver || disabled}
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showAutocomplete}
              aria-controls="album-autocomplete-listbox"
              aria-activedescendant={highlightedIndex >= 0 ? `album-option-${highlightedIndex}` : undefined}
            />

            {/* Clear button */}
            {showClearButton && (
              <button
                className={cx('clear-button')}
                onClick={handleClear}
                type="button"
                aria-label={copy?.clearButtonAriaLabel}
              >
                <IconClear aria-hidden="true" className={"game-icon"} />
              </button>
            )}

            {/* Autocomplete */}
            <AnimatePresence>
              {showAutocomplete && (
                <motion.div
                  className={cx('autocomplete-wrapper')}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: ANIMATION.AUTOCOMPLETE_FADE / 1000 }}
                >
                  <motion.div
                    className={cx('autocomplete-animator')}
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: ANIMATION.AUTOCOMPLETE_HEIGHT / 1000, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div
                      className={cx('autocomplete')}
                      role="listbox"
                      id="album-autocomplete-listbox"
                    >
                      {autocompleteItems.map((item, index) => (
                        <AutocompleteItem
                          key={`${item.album}-${index}`}
                          item={item}
                          isHighlighted={index === highlightedIndex}
                          guess={guess}
                          onSelect={handleAutocompleteSelect}
                          index={index}
                        />
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

          </div>
        </WarbleInput>

        {/* Submit button */}
        <button
          className={cx('guess-input-submit')}
          onClick={() => isValidGuess && onSubmit()}
          disabled={!isValidGuess || gameOver || disabled}
          aria-label={copy?.submitButtonAriaLabel}
        >
          <IconArrowRight aria-hidden="true" />
        </button>
      </div>

      {/* Incorrect guess toast */}
      <div className={cx('toast-incorrect')}
        ref={toastIncorrectRef}
        aria-live="polite"
        aria-atomic="true"
        aria-hidden="true"
      >
        <span className={cx('eyebrow-l')}>
          {copy?.incorrectGuess || 'Incorrect'}
        </span>
      </div>
    </div>
  );

};

export default React.memo(GameInput);
